import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Mail, Phone, MapPin, Send, MessageSquare, Globe, Clock, CheckCircle } from 'lucide-react';
import Button from '../components/atoms/Button';
import Card from '../components/atoms/Card';
import Input from '../components/atoms/Input';
import ResponsiveContainer from '../components/molecules/ResponsiveContainer';
import toast from 'react-hot-toast';

const ContactPage: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Success
      setSubmitted(true);
      toast.success('Your message has been sent successfully!');
      
      // Reset form after success
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Contact information
  const contactInfo = [
    {
      title: 'Email',
      value: 'contact@gravexlabs.com',
      icon: Mail
    },
    {
      title: 'Phone',
      value: '+1 (555) 123-4567',
      icon: Phone
    },
    {
      title: 'Address',
      value: 'San Francisco, CA',
      icon: MapPin
    }
  ];

  // FAQ items
  const faqItems = [
    {
      question: 'How can I get an API key for Aegis Vault?',
      answer: 'You can get an API key by signing up for an account and navigating to the API section in your dashboard. We offer different tiers based on your usage needs.'
    },
    {
      question: 'Do you offer enterprise solutions?',
      answer: 'Yes, we offer custom enterprise solutions with dedicated support, SLAs, and additional security features. Contact our sales team for more information.'
    },
    {
      question: 'How secure is the document storage?',
      answer: 'All documents are encrypted with AES-256 before being stored on IPFS. Only you and users you explicitly share with can access your documents. We use a zero-knowledge architecture where encryption keys never leave your device.'
    },
    {
      question: 'Can I integrate Aegis Vault with my existing systems?',
      answer: 'Yes, we offer comprehensive APIs and SDKs for JavaScript, Python, Go, and Java that allow you to integrate Aegis Vault with your existing systems.'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                icon={ArrowLeft}
                onClick={() => navigate('/')}
                className="text-gray-600 hover:text-gray-900"
              >
                Back to Home
              </Button>
              <div className="h-6 w-px bg-gray-300" />
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-lg flex items-center justify-center">
                  <MessageSquare className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Contact Us</h1>
                  <p className="text-gray-600">Get in touch with the GravexLabs team</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12">
        <ResponsiveContainer maxWidth="full" padding="none">
          <div className="space-y-16">
            {/* Hero Section */}
            <div className="text-center max-w-3xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  We'd Love to Hear From You
                </h2>
                <p className="text-xl text-gray-600 mb-8">
                  Whether you have a question about our products, pricing, or just want to say hello, we're here to help.
                </p>
                <div className="flex justify-center">
                  <div className="w-24 h-1 bg-primary-500 rounded-full"></div>
                </div>
              </motion.div>
            </div>

            {/* Contact Form and Info */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Contact Information */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="lg:col-span-1 space-y-6"
              >
                <Card className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-6">Contact Information</h3>
                  <div className="space-y-6">
                    {contactInfo.map((item, index) => (
                      <div key={index} className="flex items-start gap-4">
                        <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <item.icon className="h-5 w-5 text-primary-600" />
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">{item.title}</h4>
                          <p className="text-gray-600">{item.value}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>

                <Card className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-6">Business Hours</h3>
                  <div className="space-y-4">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Clock className="h-5 w-5 text-primary-600" />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">Customer Support</h4>
                        <p className="text-gray-600">Monday - Friday: 9am - 5pm PST</p>
                        <p className="text-gray-600">Saturday - Sunday: Closed</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Globe className="h-5 w-5 text-primary-600" />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">Global Support</h4>
                        <p className="text-gray-600">We offer support in multiple languages and time zones.</p>
                      </div>
                    </div>
                  </div>
                </Card>
              </motion.div>

              {/* Contact Form */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="lg:col-span-2"
              >
                <Card className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-6">Send Us a Message</h3>
                  
                  {submitted ? (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircle className="h-8 w-8 text-green-600" />
                      </div>
                      <h4 className="text-xl font-semibold text-gray-900 mb-2">Message Sent!</h4>
                      <p className="text-gray-600 mb-6">
                        Thank you for reaching out. We'll get back to you as soon as possible.
                      </p>
                      <Button
                        variant="outline"
                        onClick={() => setSubmitted(false)}
                      >
                        Send Another Message
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Input
                          label="Your Name"
                          type="text"
                          value={formData.name}
                          onChange={(e) => handleInputChange('name', e.target.value)}
                          placeholder="John Doe"
                          required
                        />
                        <Input
                          label="Email Address"
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          placeholder="john@example.com"
                          required
                        />
                      </div>
                      <Input
                        label="Subject"
                        type="text"
                        value={formData.subject}
                        onChange={(e) => handleInputChange('subject', e.target.value)}
                        placeholder="How can we help you?"
                        required
                      />
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Message
                        </label>
                        <textarea
                          value={formData.message}
                          onChange={(e) => handleInputChange('message', e.target.value)}
                          rows={6}
                          className="w-full rounded-lg border-2 border-gray-200 px-3 py-2 text-sm
                            placeholder-gray-400 transition-all duration-200
                            focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-opacity-20"
                          placeholder="Tell us how we can help..."
                          required
                        ></textarea>
                      </div>
                      <Button
                        type="submit"
                        variant="primary"
                        icon={Send}
                        loading={isSubmitting}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? 'Sending...' : 'Send Message'}
                      </Button>
                    </form>
                  )}
                </Card>
              </motion.div>
            </div>

            {/* FAQ Section */}
            <div>
              <div className="text-center mb-12">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h3>
                <p className="text-gray-600 max-w-2xl mx-auto">
                  Find quick answers to common questions about Aegis Vault and our services.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {faqItems.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 * index }}
                  >
                    <Card className="p-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-3">{item.question}</h4>
                      <p className="text-gray-600">{item.answer}</p>
                    </Card>
                  </motion.div>
                ))}
              </div>
              <div className="text-center mt-8">
                <p className="text-gray-600 mb-4">
                  Don't see your question here? Reach out to our support team.
                </p>
                <Button
                  variant="outline"
                  onClick={() => {
                    const contactForm = document.querySelector('form');
                    if (contactForm) {
                      contactForm.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                >
                  Contact Support
                </Button>
              </div>
            </div>

            {/* Map or Location */}
            <div>
              <Card className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-6">Our Location</h3>
                <div className="aspect-video bg-gray-200 rounded-lg overflow-hidden">
                  {/* In a real implementation, this would be a Google Map or similar */}
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-r from-gray-100 to-gray-200">
                    <div className="text-center">
                      <MapPin className="h-12 w-12 text-primary-500 mx-auto mb-4" />
                      <h4 className="text-lg font-semibold text-gray-900">GravexLabs Headquarters</h4>
                      <p className="text-gray-600">San Francisco, California</p>
                      <p className="text-gray-500 text-sm mt-2">Remote-first company with team members worldwide</p>
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            {/* Connect Section */}
            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Connect With Us</h3>
              <p className="text-gray-600 max-w-2xl mx-auto mb-8">
                Follow us on social media for the latest updates, security tips, and more.
              </p>
              <div className="flex justify-center gap-4">
                <Button variant="outline" size="lg">
                  Twitter
                </Button>
                <Button variant="outline" size="lg">
                  LinkedIn
                </Button>
                <Button variant="outline" size="lg">
                  GitHub
                </Button>
              </div>
            </div>
          </div>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default ContactPage;